import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from 'vue';
import { pick } from 'lodash';
import md5 from 'md5';
import { useAcl, useApiFieldErrors, useI18n, usePlanLimits, useReportForm, useReportUtils, useTutorialIntro, useTutorialApi, type PlanLimitsProp } from '@/composables';
import { $emitter } from '@/services/emitter';
import {
  getTrafficSourceApiRequest,
  postCreateTrafficSourceApiRequest,
  putUpdateTrafficSourceApiRequest,
} from '@/services/api/traffic-sources-api';
import { populateTagsStoreCacheApiRequest } from '@/services/api/api-services/tags';
import { refreshUserProfileFromApi } from '@/services/api/api-services/load-profile';
import { useStore } from '@/store';
import { populateIntegrationsStoreCacheApiRequest } from '@/services/api/api-services/integrations';
import { showPlanLimitsErrorInToast } from '@/services/constants/errors-without-toast';

export type TrafficSourceDialogMode = 'create' | 'edit' | 'duplicate';

export type TrafficSourceDialogData = {
  mode?: TrafficSourceDialogMode;
  id?: string;
  item?: Record<string, unknown>;
  [key: string]: unknown;
};

type TrafficSourceForm = Record<string, unknown> & {
  postbackUrl: string;
  status: string;
  tags?: unknown[];
  integrationId: string;
  trafficLossPercent: number;
  useTrafficLoss: boolean;
  workspaceId?: string;
  name?: string;
};

const CHIPS_BASE = [
  '+ {externalId}',
  '+ {payout}',
  '+ {campaignCpa}',
  '+ {campaignId}',
  '+ {trafficSourceId}',
  '+ {landingId}',
  '+ {offerId}',
  '+ {deviceType}',
  '+ {deviceVendor}',
  '+ {deviceModel}',
  '+ {browser}',
  '+ {browserVersion}',
  '+ {os}',
  '+ {osVersion}',
  '+ {country}',
  '+ {countryCode}',
  '+ {city}',
  '+ {region}',
  '+ {isp}',
  '+ {connectionType}',
  '+ {mobileCarrier}',
  '+ {ip}',
  '+ {referrerDomain}',
  '+ {lang}',
  '+ {transactionId}',
  '+ {clickId}',
  '+ {custom1}',
  '+ {custom2}',
  '+ {customN}',
  '+ {payout.CURRENCY}',
  '+ {status}',
  '+ {userAgent}',
  '+ {timestamp}',
  '+ {cet}',
  '+ {offerName}',
  '+ {offerCurrency}',
];

const EDIT_TRAFFIC_SOURCE_FIELDS = [
  'name',
  'status',
  'postbackUrl',
  'extClickIdParam',
  'extClickIdPlaceholder',
  'costParam',
  'costPlaceholder',
  'created',
  'updated',
  'updatedBy',
  'custom1Param',
  'custom1Placeholder',
  'custom1Name',
  'custom1Masking',
  'custom2Param',
  'custom2Placeholder',
  'custom2Name',
  'custom2Masking',
  'custom3Param',
  'custom3Placeholder',
  'custom3Name',
  'custom3Masking',
  'custom4Param',
  'custom4Placeholder',
  'custom4Name',
  'custom4Masking',
  'custom5Param',
  'custom5Placeholder',
  'custom5Name',
  'custom5Masking',
  'custom6Param',
  'custom6Placeholder',
  'custom6Name',
  'custom6Masking',
  'custom7Param',
  'custom7Placeholder',
  'custom7Name',
  'custom7Masking',
  'custom8Param',
  'custom8Placeholder',
  'custom8Name',
  'custom8Masking',
  'custom9Param',
  'custom9Placeholder',
  'custom9Name',
  'custom9Masking',
  'custom10Param',
  'custom10Placeholder',
  'custom10Name',
  'custom10Masking',
  'trafficLossPercent',
  'trackImpressions',
  'useTrafficLoss',
  'postbackSendConfirmStatus',
  'postbackSendNewStatus',
  'postbackSendChargebackStatus',
  'postbackSendDeclineStatus',
  'workspaceId',
  'postbackPercent',
  'tags',
  'integrationId',
] as const;

const DUPLICATE_TRAFFIC_SOURCE_FIELDS = [
  'name',
  'status',
  'postbackUrl',
  'extClickIdParam',
  'extClickIdPlaceholder',
  'costParam',
  'costPlaceholder',
  'custom1Param',
  'custom1Placeholder',
  'custom1Name',
  'custom2Param',
  'custom2Placeholder',
  'custom2Name',
  'custom3Param',
  'custom3Placeholder',
  'custom3Name',
  'custom4Param',
  'custom4Placeholder',
  'custom4Name',
  'custom5Param',
  'custom5Placeholder',
  'custom5Name',
  'custom6Param',
  'custom6Placeholder',
  'custom6Name',
  'custom7Param',
  'custom7Placeholder',
  'custom7Name',
  'custom8Param',
  'custom8Placeholder',
  'custom8Name',
  'custom9Param',
  'custom9Placeholder',
  'custom9Name',
  'custom10Param',
  'custom10Placeholder',
  'custom10Name',
  'trafficLossPercent',
  'trackImpressions',
  'useTrafficLoss',
  'postbackSendConfirmStatus',
  'postbackSendNewStatus',
  'postbackSendChargebackStatus',
  'postbackSendDeclineStatus',
  'workspaceId',
  'postbackPercent',
  'tags',
  'integrationId',
] as const;

const defaultForm = (): TrafficSourceForm => ({
  postbackUrl: '',
  status: 'active',
  extClickIdParam: '',
  extClickIdPlaceholder: '',
  costParam: '',
  costPlaceholder: '',
  custom1Param: '',
  custom1Placeholder: '',
  custom1Name: '',
  custom1Masking: false,
  custom2Param: '',
  custom2Placeholder: '',
  custom2Name: '',
  custom2Masking: false,
  custom3Param: '',
  custom3Placeholder: '',
  custom3Name: '',
  custom3Masking: false,
  custom4Param: '',
  custom4Placeholder: '',
  custom4Name: '',
  custom4Masking: false,
  custom5Param: '',
  custom5Placeholder: '',
  custom5Name: '',
  custom5Masking: false,
  custom6Param: '',
  custom6Placeholder: '',
  custom6Name: '',
  custom6Masking: false,
  custom7Param: '',
  custom7Placeholder: '',
  custom7Name: '',
  custom7Masking: false,
  custom8Param: '',
  custom8Placeholder: '',
  custom8Name: '',
  custom8Masking: false,
  custom9Param: '',
  custom9Placeholder: '',
  custom9Name: '',
  custom9Masking: false,
  custom10Param: '',
  custom10Placeholder: '',
  custom10Name: '',
  custom10Masking: false,
  trafficLossPercent: 0,
  trackImpressions: false,
  useTrafficLoss: false,
  integrationId: '',
  postbackSendConfirmStatus: true,
  postbackSendNewStatus: false,
  postbackSendChargebackStatus: false,
  postbackSendDeclineStatus: false,
  postbackPercent: 100,
});

export const useTrafficSourceDialog = (
  mode: TrafficSourceDialogMode,
  getData: () => TrafficSourceDialogData,
  emitClose: () => void,
) => {
  const store = useStore();
  const { t } = useI18n();
  const { aclReadOnly } = useAcl();
  const planLimitsControl = usePlanLimits();

  const edit = ref(mode === 'edit');
  const show = ref(false);
  const step = ref(1);
  const submitLoading = ref(false);
  const trafficSourceId = ref('');
  const oldHash = ref('');
  const form = ref<TrafficSourceForm>(defaultForm());
  const chips = ref<Array<{ tag: string; disabled: boolean; title: string }>>([]);
  const textarea = ref<{ $el?: HTMLElement } | null>(null);
  const touchedDictionary = ref<Record<string, boolean>>({});
  const disabled = ref({
    extClickIdParam: false,
    extClickIdPlaceholder: false,
  });

  const labelHelpExternalID = ref(
    t('Use this parameter to track external unique IDs. This is typically used when you need to return an ID to the traffic source.'),
  );

  const reportForm = useReportForm(() => ({
    edit: edit.value,
    mode: edit.value ? 'edit' : 'create',
  }));

  const introStepsDefault = computed(() => ({
    2: `<div class='introjs-tooltip__title'>${t('Traffic Source Settings')}</div><div class='introjs-tooltip__content'>${t('Specify name of traffic source')}</div>`,
    3: `<div class='introjs-tooltip__title'>${t('Traffic Source Settings')}</div><div class='introjs-tooltip__content'>${t('Create traffic source element')}</div>`,
  }));

  const tutorialIntro = useTutorialIntro(() => ({
    introStepsDefault: introStepsDefault.value,
    quickStartSection: 'traffic-sources',
  }));

  const tutorial = useTutorialApi();

  const {
    errors,
    errorsControlActive,
    errorsDict,
    clearErrors,
    applyApiErrors,
  } = useApiFieldErrors();

  const reportUtilsApi = useReportUtils({});

  const hasPlanLimitsRestriction = (limitKey: PlanLimitsProp) =>
    planLimitsControl.hasPlanLimitsRestriction(limitKey);

  const integrationsPlanLimits = (condition: string) =>
    planLimitsControl.integrationsPlanLimits(condition);

  const modalTitle = computed(() => {
    if (mode === 'edit') return t('Edit traffic source');
    if (mode === 'duplicate') return t('Duplicate traffic source');
    return t('Create traffic source');
  });

  const workspacesList = computed(() => reportForm.workspacesList.value);

  const customPlaceholders = computed(() => [
    {
      param: t('e.g. campaign_id'),
      placeholder: t("e.g. {'{'}campaign_id{'}'}"),
      name: t('e.g. Campaign ID'),
    },
    {
      param: t('e.g. category_id'),
      placeholder: t("e.g. {'{'}category_id{'}'}"),
      name: t('e.g. Category ID'),
    },
    {
      param: t('e.g. country'),
      placeholder: t("e.g. {'{'}country{'}'}"),
      name: t('e.g. Visitor country'),
    },
    {
      param: t('e.g. format'),
      placeholder: t("e.g. {'{'}format{'}'}"),
      name: t('e.g. Format'),
    },
    {
      param: t('e.g. site_id'),
      placeholder: t("e.g. {'{'}site_id{'}'}"),
      name: t('e.g. Site ID'),
    },
    {
      param: t('e.g. src_hostname'),
      placeholder: t("e.g. {'{'}src_hostname{'}'}"),
      name: t('e.g. Source domain'),
    },
    {
      param: t('e.g. variation_id'),
      placeholder: t("e.g. {'{'}variation_id{'}'}"),
      name: t('e.g. Variation ID'),
    },
    {
      param: t('e.g. zone_id'),
      placeholder: t("e.g. {'{'}zone_id{'}'}"),
      name: t('e.g. Zone ID'),
    },
    {
      param: t('e.g. parameter'),
      placeholder: t("e.g. {'{'}token{'}'}"),
      name: t('e.g. Token description'),
    },
    {
      param: t('e.g. parameter'),
      placeholder: t("e.g. {'{'}token{'}'}"),
      name: t('e.g. Token description'),
    },
  ]);

  const integration = computed(() =>
    store.cache.integrations.data.find(
      (item: { id: string }) => item.id === form.value.integrationId,
    ),
  );

  const integrationType = computed(() => integration.value?.type);

  const integrationsOptions = computed(() => {
    const result = store.cache.integrations.data.map((item: { type: string }) => ({
      ...item,
      planLimits: integrationsPlanLimits(item.type),
    }));

    return [
      { id: '', name: 'None' },
      ...result,
    ];
  });

  const isActionsDisabled = computed(() => {
    if (mode !== 'edit') return false;
    return !!aclReadOnly({
      workspaceId: form.value.workspaceId,
      id: trafficSourceId.value,
    });
  });

  const showPlanLimitsWarning_tags = computed(
    () =>
      !!form.value.tags?.length
      && planLimitsControl.hasPlanLimitsRestriction('tagsTrafficSources'),
  );

  const showPlanLimitsWarning__integrations = computed(() => {
    if (!integrationType.value) return false;
    return hasPlanLimitsRestriction(integrationsPlanLimits(integrationType.value));
  });

  const trafficLossPercent = computed({
    get: () => form.value.trafficLossPercent,
    set: (value: number | string) => {
      form.value.trafficLossPercent = Number((+value).toFixed(6).toString());
    },
  });

  const useTrafficLoss = computed({
    get: () => form.value.useTrafficLoss,
    set: (value: boolean) => {
      form.value.trafficLossPercent = 0;
      form.value.useTrafficLoss = value;
    },
  });

  const getDefaultChips = () =>
    CHIPS_BASE.map((tag) => ({
      tag,
      disabled: false,
      title:
        tag === '+ {payout}'
          ? t('With this token selected the conversion payout value will bе passed to the traffic source via the postback URL.')
          : '',
    }));

  const getCurrentHash = () => md5(JSON.stringify(form.value));

  const changePostbackUrl = () => {
    chips.value.forEach((item) => {
      const disItem = item.tag.replace('+ ', '');
      item.disabled = !!(form.value.postbackUrl && form.value.postbackUrl.indexOf(disItem) + 1);
    });
  };

  const changeDepends = (value: string, ...depends: string[]) => {
    depends.forEach((item, index) => {
      if (touchedDictionary.value[item]) {
        if (!value && !form.value[item]) {
          touchedDictionary.value[item] = false;
        }
      } else {
        const itemValue = index === 0 && value ? `{${value}}` : value;
        form.value[item] = itemValue;
      }
    });
  };

  const touchDepends = (name: string) => {
    touchedDictionary.value[name] = true;
  };

  const clickChip = (item: { tag: string; disabled: boolean }) => {
    if (item.disabled) return;

    const textArea = textarea.value?.$el?.getElementsByTagName('textarea');
    if (!textArea?.[0]) return;

    const startPos = textArea[0].selectionStart;
    const endPos = textArea[0].selectionEnd;
    const tmpStr = textArea[0].value;
    form.value.postbackUrl =
      `${tmpStr.substring(0, startPos)}${item.tag}${tmpStr.substring(endPos, tmpStr.length)}`.replace(
        '+ ',
        '',
      );
    changePostbackUrl();
    textArea[0].focus();
  };

  const showPlanLimitsWarning_mask = ({ key }: { key: string }) =>
    !!form.value[key] && hasPlanLimitsRestriction('trafficSourceParamMasking');

  const processForm = (source: TrafficSourceForm) => {
    const preparedForm = { ...source };
    if (integrationType.value === 'facebook-conversions') {
      preparedForm.postbackUrl = '';
    }
    preparedForm.tags = (preparedForm.tags || []).map((item) =>
      typeof item === 'object' && item && 'id' in item && (item as { id?: string }).id
        ? (item as { name: string }).name
        : item,
    );
    return preparedForm;
  };

  const applySubmitErrors = (error: unknown) => {
    const axiosError = error as { message?: string; response?: { status?: number } };
    if (
      axiosError.response?.status === 403
      || axiosError.response?.status === 400
      || axiosError.message === 'Network Error'
    ) {
      return;
    }
    if (axiosError.response?.status === 409 && showPlanLimitsErrorInToast(error)) {
      return;
    }
    if (!axiosError.response) {
      return;
    }

    applyApiErrors(error);
  };

  const loadTrafficSourceFromApi = async (id: string) => {
    const data = await getTrafficSourceApiRequest(id);
    const fields =
      mode === 'edit' ? EDIT_TRAFFIC_SOURCE_FIELDS : DUPLICATE_TRAFFIC_SOURCE_FIELDS;
    form.value = pick(data, fields) as TrafficSourceForm;
    if (!form.value.integrationId) {
      form.value.integrationId = '';
    }
    return data;
  };

  const loadCreateForm = () =>
    reportForm.loadForm({
      defaultForm,
      form: form.value,
    });

  const initializeChips = () => {
    chips.value = getDefaultChips();
    changePostbackUrl();
  };

  const loadData = async () => {
    const data = getData();

    if (mode === 'edit') {
      edit.value = true;
      trafficSourceId.value = data.id || (store.getTableSelectedItem().id as string);
      store.setLoading(true);
      try {
        await loadTrafficSourceFromApi(trafficSourceId.value);
        initializeChips();
        show.value = true;
        oldHash.value = getCurrentHash();
      } finally {
        store.setLoading(false);
      }
      return;
    }

    if (mode === 'duplicate') {
      store.setLoading(true);
      try {
        const sourceId = store.getTableSelectedItem().id as string;
        await loadTrafficSourceFromApi(sourceId);
        form.value.name = `${form.value.name} (${t('____x.copy')})`;
        trafficSourceId.value = '';
        initializeChips();
        oldHash.value = getCurrentHash();
        show.value = true;
      } finally {
        store.setLoading(false);
      }
      return;
    }

    store.setLoading(true);
    try {
      await loadCreateForm();
      if (data.item) {
        form.value = {
          ...form.value,
          ...data.item,
        } as TrafficSourceForm;
      }
      initializeChips();
      tutorial.form();
      oldHash.value = getCurrentHash();
      show.value = true;
    } finally {
      store.setLoading(false);
    }
  };

  const onBusOpenCreateTrafficSource = (payload: unknown) => {
    $emitter.emit('open-dialog', {
      name: 'TrafficSourceDialog',
      data: { mode: 'create', ...(payload as Record<string, unknown>) },
    });
  };

  const close = () => {
    if (mode === 'create') {
      $emitter.off('open-create-traffic-source', onBusOpenCreateTrafficSource);
    }
    emitClose();
  };

  const checkClose = () => {
    if (oldHash.value !== getCurrentHash() && !isActionsDisabled.value) {
      $emitter.emit('open-dialog', {
        name: 'ConfirmDialog',
        data: { onConfirm: () => close() },
      });
    } else {
      close();
    }
  };

  const clearTags = () => {
    form.value.tags = [];
  };

  const submit = async () => {
    store.setLoading(true);
    submitLoading.value = true;
    clearErrors();

    try {
      if (mode === 'edit') {
        const result = await putUpdateTrafficSourceApiRequest(
          trafficSourceId.value,
          processForm(form.value),
        );
        await refreshUserProfileFromApi();
        oldHash.value = getCurrentHash();
        reportUtilsApi.updateName('traffic-sources', result.id as string);
        $emitter.emit('refresh-report');
        $emitter.emit('toast-success', t('Traffic Source has been changed'));
        close();
        return;
      }

      await postCreateTrafficSourceApiRequest(processForm(form.value));
      await refreshUserProfileFromApi();
      oldHash.value = getCurrentHash();
      $emitter.emit('refresh-report');
      $emitter.emit('toast-success', t('Traffic Source has been saved'));
      close();
    } catch (error) {
      applySubmitErrors(error);
    } finally {
      store.setLoading(false);
      submitLoading.value = false;
    }
  };

  watch(integrationType, (value, oldValue) => {
    if (value === 'facebook-conversions') {
      form.value.extClickIdParam = 'fbclid';
      form.value.extClickIdPlaceholder = '{fbclid}';
      disabled.value.extClickIdParam = true;
      disabled.value.extClickIdPlaceholder = true;
      labelHelpExternalID.value = t(
        'fbclid is essential for conversion tracking between BeMob and Facebook.<br>If you wish to delete the value from the field, you will need to remove the integration with the Facebook from this template.',
      );
      return;
    }

    if (oldValue === 'facebook-conversions') {
      form.value.extClickIdParam = '';
      form.value.extClickIdPlaceholder = '';
      disabled.value.extClickIdParam = false;
      disabled.value.extClickIdPlaceholder = false;
      labelHelpExternalID.value = t(
        'Use this parameter to track external unique IDs. This is typically used when you need to return an ID to the traffic source.',
      );
    }
  });

  onMounted(async () => {
    if (mode === 'create') {
      $emitter.on('open-create-traffic-source', onBusOpenCreateTrafficSource);
    }

    await loadData();

    if (!store.cache.tags['traffic-sources'].isUploaded) {
      await populateTagsStoreCacheApiRequest({ type: 'traffic-sources' });
    }

    if (!store.cache.integrations.isUploaded) {
      await populateIntegrationsStoreCacheApiRequest();
    }
  });

  onUnmounted(() => {
    if (mode === 'create') {
      $emitter.off('open-create-traffic-source', onBusOpenCreateTrafficSource);
    }
  });

  return {
    store,

    t,
    show,
    step,
    submitLoading,
    form,
    chips,
    textarea,
    disabled,
    labelHelpExternalID,
    errors,
    errorsControlActive,
    errorsDict,
    modalTitle,
    workspacesList,
    customPlaceholders,
    integrationType,
    integrationsOptions,
    isActionsDisabled,
    showPlanLimitsWarning_tags,
    showPlanLimitsWarning__integrations,
    trafficLossPercent,
    useTrafficLoss,
    hasPlanLimitsRestriction,
    showPlanLimitsWarning_mask,
    tutorial,
    tutorialIntro,
    onChangeWorkspace: () => {},
    changePostbackUrl,
    changeDepends,
    touchDepends,
    clickChip,
    checkClose,
    clearTags,
    submit,
    getIntroParams: tutorialIntro.getIntroParams,
  };
};
